import { DAYS_PER_WEEK, MINUTES_PER_HOUR, HOURS_PER_DAY } from "@/constants/time";
import { DAILY_HOURS_INPUT_REGEX } from "@/features/dashboard/constants/dailyHoursInput";
import { convertFullWidthToHalfWidth } from "@/utils/pieces";

/**
 * Normalizes a raw daily-hours input string for validation/save WITHOUT mutating what
 * the user typed: full-width → half-width, then the decimal-format regex gate.
 * Returns NaN for anything that isn't a well-formed daily-hours number (e.g. "。", "abc").
 */
export function parseDailyHoursInput(input: string): number {
  // Fold every decimal-separator variant to "." then Number() can parse it. Note Moji
  // rewrites the ideographic period 。(U+3002) to its half-width form ｡(U+FF61), so we
  // must catch that too, alongside the full-width full stop ．(U+FF0E).
  const normalized = convertFullWidthToHalfWidth(input.trim()).replace(/[．。｡]/g, ".");
  if (!DAILY_HOURS_INPUT_REGEX.test(normalized)) return NaN;
  return Number(normalized);
}

/**
 * Validates daily hours input: must be a number, > 0, <= HOURS_PER_DAY
 * Type predicate: narrows unknown to number when true
 */
export function isValidDailyHours(num: unknown): num is number {
  return typeof num === "number" && !isNaN(num) && num > 0 && num <= HOURS_PER_DAY;
}

/**
 * Calculates average daily hours from a total weekly minutes target.
 * Returns 0 for non-positive or non-finite input.
 */
export const calculateDailyHoursFromWeeklyMinutes = (weeklyMinutes: number): number => {
  if (!isFinite(weeklyMinutes) || weeklyMinutes <= 0) return 0;
  return weeklyMinutes / MINUTES_PER_HOUR / DAYS_PER_WEEK;
};

/**
 * Calculates total weekly minutes from a daily study hours target.
 * Returns 0 for non-positive or non-finite input.
 */
export const calculateWeeklyMinutesFromDailyHours = (dailyHours: number): number => {
  if (!isFinite(dailyHours) || dailyHours <= 0) return 0;
  return Math.round(dailyHours * MINUTES_PER_HOUR * DAYS_PER_WEEK);
};
